// src/App.tsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { DashboardsPage } from './pages/DashboardsPage';
import { BookmarksPage } from './pages/BookmarksPage';
import { DashboardsByConfigPage } from './pages/DashboardsByConfigPage';
import { OrganizePage } from './pages/OrganizePage';
import { ProjectDashboardContent } from './pages/ProjectDashboardPage';
import { ComponentsTestPage } from './pages/ComponentsTestPage';
import { HomeViewContent } from './pages/HomeView';
import { TenantProvider } from './contexts/TenantContext';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { ProtectedRoute } from './components/Auth/ProtectedRoute';
import { MainLayout } from './components/Layout/MainLayout';
import { NewPagePage } from './pages/NewPagePage';
import { TenantRegistrationPage } from './pages/TenantRegistration';
import { InitialWizardPage, UpdatePasswordPage } from './pages/InitialWizard';
import { config as appConfig } from './config/env';
import { ToastProvider } from './components/ui/Toast';
import { TopLoadingBar } from './components/ui/TopLoadingBar/TopLoadingBar';
import { GlobalTooltip } from './components/utils/GlobalTooltip';
import { ReportWrapper } from './components/utils/ReportWrapper';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { authStorage } from './lib/storage';
import { dashboardService } from './services/dashboardService';
import CustomFieldsTestPage from './pages/CustomFieldsTestPage';
import ReactionsTestPage from './pages/ReactionsTestPage';
import ConversationItemTestPage from './pages/ConversationItemTestPage';
import ConversationDetailsTestPage from './pages/ConversationDetailsTestPage';
import TabControlTestPage from './pages/TabControlTestPage';
import DashboardGroupsTestPage from './pages/DashboardGroupsTestPage';
import SummernoteTestPage from './pages/SummernoteTestPage';
import EditCardTestPage from './pages/EditCardTestPage';
import DndTestPage from './pages/DndTestPage';
import GridViewTestPage from './pages/GridViewTestPage';
import ImageEditorTestPage from './pages/ImageEditorTestPage';
import { CalendarPage } from './pages/CalendarPage';
import { ProfilePage } from './pages/ProfilePage';
import { ConversationDetailsPage } from './pages/ConversationDetailsPage';
import { FileImagePage } from './pages/FileImagePage';
import { FileVideoPage } from './pages/FileVideoPage';
import { FilePdfPage } from './pages/FilePdfPage';
import { RecycleBinPage } from './pages/RecycleBinPage';
import VacationsRequestPage from './pages/VacationsRequestPage';
import VacationsApprovalPage from './pages/VacationsApprovalPage';
import PTOPermissionsPage from './pages/PTOPermissionsPage';
import CorgeeStorageHelper from './components/Globals/CorgeeStorageHelper';
import DisabledUserGuard from './components/Globals/DisabledUserGuard';
import PTOReport from './pages/PTOReport';
import { SharedDashboardPage } from './pages/SharedDashboardPage';
import { AdminConsolePage } from './pages/AdminConsolePage';
import { AddPeoplePage } from './pages/AddPeoplePage';
import UserRequestPage from './pages/UserRequestPage';
import AttendanceReport from './pages/AttendanceReport/AttendanceReport';
import EfficiencyReport from './pages/EfficiencyReport/EfficiencyReport';
import CapturedTimeReport from './pages/CapturedTimeReport/CapturedTimeReport';
import TimeTrackingReportPage from './pages/TimeTrackingReport/TimeTrackingReportPage';
import MonthlyPerformanceReport from './pages/MonthlyPerformanceReport/MonthlyPerformanceReport';
import AvailabilityReport from './pages/AvailabilityReport/AvailabilityReport';
import ETACostReport from './pages/ETACostReport/ETACostReport';
import ImportFromAsana from './pages/ImportFromAsana/ImportFromAsana';
import ArtistReport from './pages/ArtistReport/ArtistReport';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const PTOReportWithId: React.FC = () =>
{
  const { vacationRequestId } = useParams<{ vacationRequestId: string }>();
  return (
    <PTOReport
      config={{
        authToken: CorgeeStorageHelper.getToken(),
        url: CorgeeStorageHelper.apiURL(),
        requestId: vacationRequestId,
      }}
    />
  );
};

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * First login of a new tenant: the API asks for a password change and, until the
 * initial wizard is completed, "initialCustomize" is false.
 */
export const getFirstLoginPath = (): string | null =>
{
  if (localStorage.getItem('C2_changePasswordConfig'))
  {
    return '/UpdatePassword';
  }
  if (localStorage.getItem('C2_initialCustomize') === 'false')
  {
    return '/InitialWizard';
  }
  return null;
};

/**
 * RootRoute: si hay token redirige a /home o /Dashboards según isBeta,
 * si no hay token muestra LoginPage.
 */
const RootRoute: React.FC = () =>
{
  const accessToken = authStorage.getAccessToken();
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const [checked, setChecked] = useState(!accessToken);

  useEffect(() =>
  {
    if (!accessToken) return;
    const firstLoginPath = getFirstLoginPath();
    if (firstLoginPath)
    {
      setRedirectPath(firstLoginPath);
      setChecked(true);
      return;
    }
    dashboardService
      .getIsBeta()
      .then(info =>
      {
        localStorage.setItem('C2_isBeta', String(info.isBeta));
        setRedirectPath(info.isBeta ? '/home' : '/Dashboards');
      })
      .catch(() =>
      {
        const isBeta = localStorage.getItem('C2_isBeta') === 'true';
        setRedirectPath(isBeta ? '/home' : '/Dashboards');
      })
      .finally(() => setChecked(true));
  }, [accessToken]);

  // Redirect legacy hash-based shared URLs — all hooks must be called before these early returns.
  const hash = window.location.hash;

  // /#/Dashboard/:dashboardId/:configValueId  →  /Dashboard/:dashboardId/:configValueId
  const sharedMatch = hash.match(/^#\/Dashboard\/(\d+)\/(\d+)/);
  if (sharedMatch)
  {
    return <Navigate to={`/Dashboard/${sharedMatch[1]}/${sharedMatch[2]}`} replace />;
  }

  // /#/ProjectDetails/:projectId?ProjectDashboardId=:id&...  →  /ProjectDetails/:projectId (with state)
  const projectMatch = hash.match(/^#\/ProjectDetails\/(\d+)/);
  if (projectMatch)
  {
    const queryString = hash.slice(hash.indexOf('?') + 1);
    const params = new URLSearchParams(hash.includes('?') ? queryString : '');
    const dashboardId = params.get('ProjectDashboardId');
    const state = dashboardId ? { sharedDashboardId: parseInt(dashboardId, 10) } : undefined;
    return <Navigate to={`/ProjectDetails/${projectMatch[1]}`} state={state} replace />;
  }

  if (!checked)
  {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5dade2]"></div>
      </div>
    );
  }

  if (redirectPath)
  {
    return <Navigate to={redirectPath} replace />;
  }

  return <LoginPage />;
};

function App()
{
  return (
    <DndProvider backend={HTML5Backend}>
      <DarkModeProvider>
        <QueryClientProvider client={queryClient}>
          <TopLoadingBar />
          <GlobalTooltip />
          <ToastProvider>
            <AuthProvider>
              <TenantProvider>
                <SocketProvider>
                  <DisabledUserGuard />
                  <BrowserRouter basename={import.meta.env.VITE_BASE_URL || '/'}>
                    <Routes>
                      {/* Root route - redirects based on auth + isBeta */}
                      <Route path="/" element={<RootRoute />} />

                      {/* First login of a new tenant: forced password change + initial wizard */}
                      <Route path="/UpdatePassword" element={<UpdatePasswordPage />} />
                      <Route
                        path="/InitialWizard"
                        element={
                          <ProtectedRoute>
                            <InitialWizardPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Tenant registration console - alternate URL enabled via .env */}
                      {appConfig.tenantRegistration.enabled && (
                        <Route
                          path={appConfig.tenantRegistration.path}
                          element={<TenantRegistrationPage />}
                        />
                      )}

                      {/* Protected routes with shared MainLayout */}
                      <Route
                        element={
                          <ProtectedRoute>
                            <MainLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route path="/home" element={<HomeViewContent />} />
                        <Route path="/Dashboards" element={<DashboardsPage />} />
                        <Route
                          path="/Dashboards/:configValueId"
                          element={<DashboardsByConfigPage />}
                        />
                        <Route
                          path="/Dashboard/:dashboardId/:configValueId"
                          element={<SharedDashboardPage />}
                        />
                        <Route path="/Organize/:dashboardId" element={<OrganizePage />} />
                        <Route path="/Bookmarks" element={<BookmarksPage />} />
                        <Route
                          path="/ProjectDetails/:projectId?"
                          element={<ProjectDashboardContent />}
                        />
                        <Route path="/newpage" element={<NewPagePage />} />
                        <Route path="/Calendar" element={<CalendarPage />} />
                        <Route path="/VacationsRequest" element={<VacationsRequestPage />} />
                        <Route path="/VacationsApproval" element={<VacationsApprovalPage />} />
                        <Route path="/PTOPermissions" element={<PTOPermissionsPage />} />
                        <Route
                          path="/PTOReport"
                          element={
                            <ReportWrapper>
                              <PTOReport
                                config={{
                                  authToken: CorgeeStorageHelper.getToken(),
                                  url: CorgeeStorageHelper.apiURL(),
                                }}
                              />
                            </ReportWrapper>
                          }
                        />
                        <Route
                          path="/PTOReport/:vacationRequestId"
                          element={
                            <ReportWrapper>
                              <PTOReportWithId />
                            </ReportWrapper>
                          }
                        />
                        <Route
                          path="/CapturedTimeReport"
                          element={
                            <ReportWrapper>
                              <CapturedTimeReport />
                            </ReportWrapper>
                          }
                        />
                        <Route
                          path="/TimeTrackingReport"
                          element={
                            <ReportWrapper>
                              <TimeTrackingReportPage />
                            </ReportWrapper>
                          }
                        />
                        <Route
                          path="/AttendanceReport"
                          element={
                            <ReportWrapper>
                              <AttendanceReport />
                            </ReportWrapper>
                          }
                        />
                        <Route
                          path="/EfficiencyReport"
                          element={
                            <ReportWrapper>
                              <EfficiencyReport />
                            </ReportWrapper>
                          }
                        />
                        <Route
                          path="/MonthlyPerformanceReport"
                          element={
                            <ReportWrapper>
                              <MonthlyPerformanceReport />
                            </ReportWrapper>
                          }
                        />
                        <Route
                          path="/AvailabilityReport"
                          element={
                            <ReportWrapper>
                              <AvailabilityReport />
                            </ReportWrapper>
                          }
                        />
                        <Route
                          path="/ETACostReport"
                          element={
                            <ReportWrapper>
                              <ETACostReport />
                            </ReportWrapper>
                          }
                        />
                        <Route
                          path="/ImportFromAsana"
                          element={
                            <ReportWrapper>
                              <ImportFromAsana />
                            </ReportWrapper>
                          }
                        />
                        <Route
                          path="/ArtistReport"
                          element={
                            <ReportWrapper>
                              <ArtistReport />
                            </ReportWrapper>
                          }
                        />
                        <Route path="/Profile" element={<ProfilePage />} />
                        <Route path="/RecycleBin" element={<RecycleBinPage />} />
                        <Route path="/AdminConsole" element={<AdminConsolePage />} />
                        <Route path="/AddPeople" element={<AddPeoplePage />} />
                        <Route path="/Person/Authorize/:personId" element={<UserRequestPage />} />
                      </Route>

                      {/* Other protected routes without shared layout */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <DashboardPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/components-test"
                        element={
                          <ProtectedRoute>
                            <ComponentsTestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/customfields-test"
                        element={
                          <ProtectedRoute>
                            <CustomFieldsTestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reactions-test"
                        element={
                          <ProtectedRoute>
                            <ReactionsTestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/conversation-test"
                        element={
                          <ProtectedRoute>
                            <ConversationItemTestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/conversation-details-test"
                        element={
                          <ProtectedRoute>
                            <ConversationDetailsTestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/tabcontrol-test"
                        element={
                          <ProtectedRoute>
                            <TabControlTestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard-groups-test"
                        element={
                          <ProtectedRoute>
                            <DashboardGroupsTestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/summernote-test"
                        element={
                          <ProtectedRoute>
                            <SummernoteTestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/editcard-test"
                        element={
                          <ProtectedRoute>
                            <EditCardTestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dnd-test"
                        element={
                          <ProtectedRoute>
                            <DndTestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/gridview-test"
                        element={
                          <ProtectedRoute>
                            <GridViewTestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/image-editor-test"
                        element={
                          <ProtectedRoute>
                            <ImageEditorTestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/Dashboard/ConversationDetails"
                        element={
                          <ProtectedRoute>
                            <ConversationDetailsPage />
                          </ProtectedRoute>
                        }
                      />
                      {/* Standalone image viewer — no MainLayout */}
                      <Route
                        path="/Dashboard/FileImage"
                        element={
                          <ProtectedRoute>
                            <FileImagePage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Standalone video viewer — no MainLayout */}
                      <Route
                        path="/Dashboard/FileVideo"
                        element={
                          <ProtectedRoute>
                            <FileVideoPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Standalone PDF viewer — no MainLayout */}
                      <Route
                        path="/Dashboard/FilePdf"
                        element={
                          <ProtectedRoute>
                            <FilePdfPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </BrowserRouter>
                </SocketProvider>
              </TenantProvider>
            </AuthProvider>
          </ToastProvider>
        </QueryClientProvider>
      </DarkModeProvider>
    </DndProvider>
  );
}

export default App;
